import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import { useTerminologia } from '../hooks/useTerminologia';
import { ROL_PERFIL_ADMINISTRADOR } from '../constants/roles';
import LoadingOverlay from '../components/LoadingOverlay';
import './DashboardGeneral.scss';

const VIEW_MODE_KEY = 'dashboardGeneralViewMode';

const STOPWORDS = new Set(['de', 'del', 'la', 'las', 'los', 'el', 'en', 'y']);

const getMonograma = (nombre) => {
    const palabras = (nombre || '').trim().split(/\s+/).filter(Boolean);
    const significativas = palabras.filter((p, i) => i === 0 || !STOPWORDS.has(p.toLowerCase()));
    const iniciales = significativas.slice(0, 2).map((p) => p[0]?.toUpperCase() || '').join('');
    return iniciales || (nombre || '').slice(0, 2).toUpperCase();
};

const IconGrid = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <rect x="3" y="3" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        <rect x="13" y="3" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        <rect x="3" y="13" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        <rect x="13" y="13" width="8" height="8" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
    </svg>
);

const IconList = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
        <path d="M8 6H21M8 12H21M8 18H21" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M3 6H3.01M3 12H3.01M3 18H3.01" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
);

const IconChevron = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
        <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const DashboardGeneral = () => {
    const HOST_API = process.env.REACT_APP_URL;
    const navigate = useNavigate();
    const { seleccionarBarrio } = useContext(BarrioContext);
    const { administracionId, rolId } = useAuth();
    const t = useTerminologia();
    const esAdmin = rolId === 1 || rolId === ROL_PERFIL_ADMINISTRADOR;

    const [barrios, setBarrios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState(() => {
        try {
            const saved = localStorage.getItem(VIEW_MODE_KEY);
            return saved === 'list' ? 'list' : 'cards';
        } catch {
            return 'cards';
        }
    });

    useEffect(() => {
        if (!esAdmin) {
            navigate('/', { replace: true });
        }
    }, [esAdmin, navigate]);

    useEffect(() => {
        if (!esAdmin || !administracionId) return;

        setLoading(true);
        axios.get(`${HOST_API}api/barrios/GetBarriosConResumen?administracionId=${administracionId}`)
            .then((res) => setBarrios(res.data || []))
            .catch((err) => console.error('Error al obtener el resumen de barrios:', err))
            .finally(() => setLoading(false));
    }, [HOST_API, administracionId, esAdmin]);

    if (!esAdmin) return null;

    const handleSeleccionarBarrio = (b) => {
        seleccionarBarrio(b.barrioId, b.nombreBarrio, b.cuit, b.direccion, b.razonSocial, b.numeroCuenta, b.cuentaBancaria, b.tipoCuentaBancaria, b.alias);
        navigate('/');
    };

    const cambiarViewMode = (modo) => {
        setViewMode(modo);
        try { localStorage.setItem(VIEW_MODE_KEY, modo); } catch { /* localStorage no disponible */ }
    };

    const imagenUrl = (b) => b.imagenUrl
        ? `${HOST_API}${b.imagenUrl}`.replace(/([^:])\/\/+/g, '$1/')
        : null;

    return (
        <div className="dbg">
            <div className="dbg-header">
                <div>
                    <h4 className="dbg-title">Dashboard General</h4>
                    <p className="dbg-subtitle">Elegí un {t.barrio} para ver su Dashboard.</p>
                </div>

                {!loading && barrios.length > 0 && (
                    <div className="dbg-toggle">
                        <button
                            type="button"
                            className={viewMode === 'cards' ? 'active' : ''}
                            onClick={() => cambiarViewMode('cards')}
                            aria-label="Ver como cards"
                            title="Ver como cards"
                        >
                            <IconGrid />
                        </button>
                        <button
                            type="button"
                            className={viewMode === 'list' ? 'active' : ''}
                            onClick={() => cambiarViewMode('list')}
                            aria-label="Ver como lista"
                            title="Ver como lista"
                        >
                            <IconList />
                        </button>
                    </div>
                )}
            </div>

            <LoadingOverlay visible={loading} />
            {!loading && (barrios.length === 0 ? (
                <p>No hay {t.barrios} cargados todavía.</p>
            ) : viewMode === 'cards' ? (
                <div className="dbg-grid">
                    {barrios.map((b) => {
                        const foto = imagenUrl(b);
                        return (
                            <div
                                key={b.barrioId}
                                className={`dbg-card${!b.activo ? ' inactivo' : ''}`}
                                onClick={() => handleSeleccionarBarrio(b)}
                            >
                                <div className={`dbg-card-media${!b.activo ? ' inactivo' : ''}`}>
                                    {foto ? (
                                        <img src={foto} alt="" />
                                    ) : (
                                        <div className="placeholder">
                                            <i className="pi pi-map-marker" style={{ fontSize: '2rem', color: 'rgba(255,255,255,0.85)' }} />
                                        </div>
                                    )}
                                    <span className={`dbg-badge ${b.activo ? 'ok' : 'off'}`}>
                                        {b.activo ? 'Activo' : 'Inactivo'}
                                    </span>
                                </div>

                                <div className="dbg-card-body">
                                    <div className="dbg-card-top">
                                        <div className="dbg-name">{b.nombreBarrio}</div>
                                        <span className="dbg-uf-chip">
                                            {b.cantidadUF} {b.cantidadUF === 1 ? t.UF : `${t.UF}s`}
                                        </span>
                                    </div>
                                    <p className="dbg-address">{b.direccion || 'Sin dirección cargada'}</p>

                                    <button
                                        className="dbg-cta dbg-card-cta"
                                        onClick={(e) => { e.stopPropagation(); handleSeleccionarBarrio(b); }}
                                    >
                                        Ver Dashboard
                                        <IconChevron />
                                    </button>
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                <div className="dbg-list">
                    {barrios.map((b) => {
                        const foto = imagenUrl(b);
                        return (
                            <div
                                key={b.barrioId}
                                className={`dbg-row${!b.activo ? ' inactivo' : ''}`}
                                onClick={() => handleSeleccionarBarrio(b)}
                            >
                                {foto ? (
                                    <img className="dbg-avatar" src={foto} alt="" />
                                ) : (
                                    <div className={`dbg-avatar${!b.activo ? ' inactivo' : ''}`}>
                                        {getMonograma(b.nombreBarrio)}
                                    </div>
                                )}

                                <div className="dbg-row-info">
                                    <div className="dbg-row-top">
                                        <span className="dbg-row-name">{b.nombreBarrio}</span>
                                        <span className={`dbg-badge ${b.activo ? 'ok' : 'off'}`}>
                                            {b.activo ? 'Activo' : 'Inactivo'}
                                        </span>
                                    </div>
                                    <p className="dbg-row-address">{b.direccion || 'Sin dirección cargada'}</p>
                                </div>

                                <span className="dbg-uf-chip">
                                    {b.cantidadUF} {b.cantidadUF === 1 ? t.UF : `${t.UF}s`}
                                </span>

                                <button
                                    className="dbg-cta dbg-row-cta"
                                    onClick={(e) => { e.stopPropagation(); handleSeleccionarBarrio(b); }}
                                >
                                    Ver Dashboard
                                    <IconChevron />
                                </button>
                            </div>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

export default DashboardGeneral;
